import { DateString, PaginationSearchParams, Uuid } from '@/api/types';
import {
  AssignedUserData,
  CaseLinkType,
  CaseResourceCombined,
  CaseStatus,
  ClinicalData,
  DonorType,
  EmployeeLabels,
  OpoContactIdIsPrimaryPair,
  OrganType,
  OutcomeData,
  ServiceType,
  SurgeonCaseSummary,
} from '@/api/apis/case';
import { TMDXFile } from '@/api/apis/files';

import type { AssignedOpoContactType } from '../assigned-opo-contact.types';

export enum OpoCaseStatus {
  // Undefined = 'Undefined',
  Draft = 'Draft',
  UnderClinicalScreening = 'UnderClinicalScreening',
  CaseInitiatedForOCS = 'CaseInitiatedForOCS',
  TeamDeploymentInProgress = 'TeamDeploymentInProgress',
  AtDonorHospital = 'AtDonorHospital',
  OrganOnOCS = 'OrganOnOCS',
  OrganInTransit = 'OrganInTransit',
  OrganAtTXCenter = 'OrganAtTXCenter',
  OrganOffOCS = 'OrganOffOCS',
  NOPServiceCompleted = 'NOPServiceCompleted',
  CaseClosed = 'CaseClosed',
  CaseCanceled = 'CaseCanceled',
  TransplantNotCompleted = 'TransplantNotCompleted',
  CaseDeclined = 'CaseDeclined',
}

export enum CreationSide {
  Tmdx = 'Tmdx',
  Customer = 'Customer',
  Opo = 'Opo',
}

export enum DonorGender {
  Male = 'Male',
  Female = 'Female',
  Other = 'Other',
}

export enum CauseOfDeath {
  Anoxia = 'Anoxia',
  Cerebrovascular = 'Cerebrovascular',
  HeadTrauma = 'HeadTrauma',
  CNSTumor = 'CNSTumor',
  Other = 'Other',
}

export enum NeuroStatus {
  BrainDead = 'BrainDead',
  BrainIsNotDead = 'BrainIsNotDead',
  PendingBrainDeathDeclaration = 'PendingBrainDeathDeclaration',
}

export enum BloodGroupAbo {
  // None = 'None',
  A = 'A',
  B = 'B',
  AB = 'AB',
  O = 'O',
}

export enum OutcomeStatus {
  ClinicalScreening = 'ClinicalScreening',
  ServiceDeclined = 'ServiceDeclined',
  Declined = 'Declined',
  ServiceCompleted = 'ServiceCompleted',
}

export type OpoLocation = {
  id: Uuid;
  country: string;
  region: string | null;
  state: string | null;
  city: string | null;
  addressLine: string | null;
  postalCode: string | null;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
};

export type OpoModel = {
  id: Uuid;
  name: string;
  code: string;
  location: OpoLocation;
};

export type DonorDemographics = {
  age: number | null;
  weight: number | null; // in grams
  height: number | null; // in millimeters
  gender: DonorGender | null;
  causeOfDeath: CauseOfDeath | null;
};

export type DonorParameters = {
  neuroStatus: NeuroStatus | null;
  vasopressors: boolean | null;
  vasopressorsDetails: string | null;
  sustainedHypotension: boolean | null;
  sustainedHypotensionDetails: string | null;
};

export type DonorBlood = {
  abo: BloodGroupAbo | null;
  positiveSerologies: string | null;
};

export type LiverData = {
  totalBilirubinRecentResult1: number | null;
  totalBilirubinRecentResult2: number | null;
  aspartateAminotransferaseRecentResult1: number | null;
  aspartateAminotransferaseRecentResult2: number | null;
  alanineAminotransferaseRecentResult1: number | null;
  alanineAminotransferaseRecentResult2: number | null;
  ctScan: boolean | null;
  ctScanDetails: string | null;
  ctScanLink: string | null;
  ctScanDocuments: TMDXFile[] | null;
  willLocalSurgeonPerformOrganProcurement: boolean | null;
  localSurgeonToProcureDetails: string | null;
  sedation: string | null;
};

export type HeartData = {
  matchRunOcsCenterNames: string | null;
};

export type LungData = {
  matchRunOcsCenterNames: string | null;
  mostRecentPfRatio: number | null;
  netFluidBalanceOverPast24Hours: number | null;
  cultureSensitivityResults: string | null;
  ctScan: boolean | null;
  ctScanDetails: string | null;
  ctScanLink: string | null;
  ctScanDocuments: TMDXFile[] | null;
  sedation: string | null;
};

export type DonorData = {
  donorDemographics: DonorDemographics | null;
  donorParameters: DonorParameters | null;
  donorBlood: DonorBlood | null;
  reasonForUnallocated: string | null;
  liverData: LiverData | null;
  heartData: HeartData | null;
  lungData: LungData | null;
};

export type SplitOrgan = {
  organType: OrganType;
  matchId?: string | null;
};

export type SplitMetadata = {
  organs?: SplitOrgan[] | null;
  splitFromCaseId?: Uuid | null;
  splittedCaseIds?: Uuid[] | null;
};

export type { AssignedOpoContactType as AssignedOpoContact } from '../assigned-opo-contact.types';

export type AssignedCustomer = {
  userId: Uuid | null;
  customerId: Uuid | null;
  user: AssignedUserData;
  group: string | null;
  isPrimary: boolean | null;
  isDefaultTxUser: boolean | null;
};

export type AssignedEmployee = {
  userId: Uuid | null;
  employeeId: Uuid | null;
  user: AssignedUserData;
  group: string | null;
  isPrimary: boolean | null;
  labels: EmployeeLabels[] | null;
};

export type OpoCase = {
  caseId: Uuid;
  opoId: Uuid | null;
  caseStatus: CaseStatus;
  opoCaseStatus: OpoCaseStatus;
  creationSide: CreationSide;
  caseName: string | null;
  donorId: string | null;
  matchId: string | null;
  organType: OrganType;
  donorType: DonorType;
  serviceType: ServiceType | null;
  opoRequest: boolean;
  donorHospitalId: Uuid | null;
  donorORTime: DateString | null;
  donorHospitalTimeZone: string | null;
  txCenterId: Uuid | null;
  txCenterName: string | null;
  txCenterCode: string | null;
  serviceRegionId: Uuid | null;
  recipientHospitalDate: string | null;
  recipientHospitalTime: string | null;
  recipientHospitalTimeZone: string | null;
  caseOutcome: OutcomeStatus | null;
  outcomeData: OutcomeData | null;
  clinicalData: ClinicalData[] | null;
  surgeonCaseSummary: SurgeonCaseSummary | null;
  opoContacts: AssignedOpoContactType[] | null;
  assignedCustomers: AssignedCustomer[] | null;
  assignedEmployees: AssignedEmployee[] | null;
  resources: CaseResourceCombined[] | null;
  files: TMDXFile[] | null;
  links: CaseLinkType[] | null;
  donorData: DonorData | null;
  splitMetadata?: SplitMetadata | null;
};

export type CreateOpoCaseRequestBody = {
  caseId?: Uuid | null;
  donorId: string;
  donorHospitalId?: Uuid | null;
  donorHospitalDate?: string | null;
  donorHospitalTime?: string | null;
  donorHospitalTimeZone?: string | null;
  opoId: Uuid;
  organType: OrganType;
  donorType: DonorType;
  serviceType?: ServiceType | null;
  serviceRegionId?: Uuid | null;
  opoContactIdIsPrimaryPairs?: OpoContactIdIsPrimaryPair[];
  matchId?: string | null;
  additionalOrgans?: SplitOrgan[];
  donorDemographics?: DonorDemographics | null;
  donorParameters?: DonorParameters | null;
  donorBlood?: DonorBlood | null;
  liverData?: LiverData | null;
  heartData?: HeartData | null;
  lungData?: LungData | null;
};

export type UpdateDonorDataRequestBody = {
  donorDemographics?: DonorDemographics | null;
  donorParameters?: DonorParameters | null;
  donorBlood?: DonorBlood | null;
  reasonForUnallocated?: string | null;
  liverData?: LiverData | null;
  heartData?: HeartData | null;
  lungData?: LungData | null;
};

export type OpoCaseListFilters = PaginationSearchParams & {
  opoId?: Uuid;
  caseStatuses?: string;
  opoCaseStatuses?: string;
  assignedOpoIds?: Uuid[];
  asc?: boolean;
};
